import type { ShapeType } from '@/lib/shapes/types/shape-type'
import { getCirclePathString } from '@/lib/elements/circle-svg-path'
import { getHeartPathString } from '@/lib/elements/heart-svg-path'
import { computeShapePathWasm, initWasmModule } from '@/wasm/wasm' // WASM calls
import * as Sentry from '@sentry/react'
import type { BoundaryComputeMode } from './types/boundary-compute-mode'
import type { BoundaryComputeOptions } from './types/boundary-compute-options'

const DEFAULT_HOT_MS = 80
let lastCallAt = -Infinity

function nowMs(): number {
  return typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now()
}

function shouldUseWasm(opts?: BoundaryComputeOptions): boolean {
  const mode: BoundaryComputeMode = opts?.mode ?? 'adaptive'

  if (mode === 'js') return false
  if (mode === 'wasm') return true

  const hotMs = opts?.hotMs ?? DEFAULT_HOT_MS
  const t = nowMs()
  const isHot = t - lastCallAt < hotMs

  lastCallAt = t

  // Resizing can trigger many calls; use JS during bursts and try WASM only when infrequent.
  return !isHot
}

type Point = { x: number; y: number }

/**
 * Initialize WASM early in app lifecycle
 * Call this once when the app starts
 */
export async function initShapeWasm(): Promise<void> {
  try {
    await initWasmModule()
  } catch (error) {
    console.warn('Failed to initialize WASM, will fallback to JS:', error)
  }
}

/**
 * Regular polygon (and star, when `insetPercent > 0`) SVG path string — matches legacy
 * `page-editor`'s `ODPath.prototype.buildPathSegments` (odeditorsvg.js) exactly: vertices are
 * inscribed in the width/height ellipse, not stretched/forced to touch every edge of the
 * bounding box. Mirrors `regularPolygonCommands` in `lib/elements/shape-path-commands.ts` (kept
 * as a separate string-based implementation here rather than importing, matching this file's
 * existing pattern of a parallel JS/WASM-facing implementation of the same shape geometry).
 */
/**
 * Regular polygon/star vertex positions, already scaled to an actual width/height — the exact
 * formula `regularPolygonCommands` (lib/elements/shape-path-commands.ts, the save-path function)
 * and the Go WASM `regularPolygonPath` use, kept here as this boundary's own parallel
 * implementation, used by `regularPolygonPathString` (JS-fallback render). This does not change
 * save/load geometry, which computes from the same angular formula independently.
 */
function regularPolygonScaledPoints(
  sidesIn: number,
  insetPercent: number,
  width: number,
  height: number
): Point[] {
  const sides = Math.max(3, Math.min(100, Math.round(sidesIn)))
  const w = Math.max(0, width)
  const h = Math.max(0, height)
  const wRadius = w / 2
  const hRadius = h / 2
  const inset = Math.max(0, insetPercent)
  const halfStepDeg = 360 / sides / 2
  const apothemRatio = Math.cos((halfStepDeg * Math.PI) / 180)
  const wInner = wRadius * apothemRatio * (1 - inset / 100)
  const hInner = hRadius * apothemRatio * (1 - inset / 100)

  const pts: Point[] = []

  for (let i = 1; i <= sides; i++) {
    const angleDeg = (360 / sides) * i
    const angleRad = (angleDeg * Math.PI) / 180

    pts.push({
      x: wRadius + wRadius * Math.sin(angleRad),
      y: hRadius - hRadius * Math.cos(angleRad),
    })

    if (inset > 0) {
      const innerAngleRad = ((angleDeg + halfStepDeg) * Math.PI) / 180

      pts.push({
        x: wRadius + wInner * Math.sin(innerAngleRad),
        y: hRadius - hInner * Math.cos(innerAngleRad),
      })
    }
  }

  return pts
}

function regularPolygonPathString(
  sidesIn: number,
  insetPercent: number,
  width: number,
  height: number
): string {
  const pts = regularPolygonScaledPoints(sidesIn, insetPercent, width, height)

  if (pts.length === 0) return ''

  const parts = [`M ${pts[0].x} ${pts[0].y}`]

  for (const p of pts.slice(1)) {
    parts.push(`L ${p.x} ${p.y}`)
  }

  parts.push('Z')
  return parts.join(' ')
}

/**
 * Synchronous JS-only shape path computation (no WASM, no Sentry spans).
 * Used for drag preview and other latency-sensitive rendering where the path
 * must be available in the same render frame as the dimensions.
 */
export function getShapePathSync(type: ShapeType, width: number, height: number): string {
  switch (type) {
    case 'square':
      return `M 0 0 L ${width} 0 L ${width} ${height} L 0 ${height} Z`

    case 'circle':
      return getCirclePathString(width, height)

    case 'triangle':
      return regularPolygonPathString(3, 0, width, height)

    case 'hexagon':
      return regularPolygonPathString(6, 0, width, height)

    case 'pentagon':
      return regularPolygonPathString(5, 0, width, height)

    case 'star':
      return regularPolygonPathString(5, 50, width, height)

    case 'heart': {
      return getHeartPathString(width, height)
    }

    case 'starone':
      return regularPolygonPathString(10, 25, width, height)

    case 'startwo':
      return regularPolygonPathString(10, 60, width, height)

    default:
      return `M 0 0 L ${width} 0 L ${width} ${height} L 0 ${height} Z`
  }
}

/**
 * Boundary function that defaults to WASM for shape path computation with JS fallback.
 */
export async function getShapePath(
  type: ShapeType,
  width: number,
  height: number,
  opts?: BoundaryComputeOptions
): Promise<string> {
  return Sentry.startSpan(
    {
      name: 'shape.path.compute',
      op: 'shape.compute',
      attributes: {
        mode: opts?.mode ?? 'adaptive',
        shapeType: type,
        width,
        height,
      },
    },
    async () => {
      try {
        if (shouldUseWasm(opts)) {
          const result = await computeShapePathWasm({ type, width, height })

          Sentry.setContext('wasm', {
            success: true,
            implementation: 'go-wasm-shapes',
          })

          return result.path
        }
      } catch (error) {
        if ((opts?.mode ?? 'adaptive') !== 'js') {
          console.warn('WASM failed, falling back to JS:', error)
          Sentry.captureException(error)
          Sentry.setContext('wasm', {
            success: false,
            error: (error as Error).message,
            fallback: 'js-shapes',
          })
        }
      }

      return getShapePathSync(type, width, height)
    }
  )
}
